import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2, Plus, Palette, Globe, FileText, Phone, Settings,
  ChevronRight, Save, Trash2, GripVertical, Eye
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONOS_DISPONIBLES = [
  "Eye", "Users", "BookOpen", "Calendar", "Shield", "Award", "FileText",
  "ShoppingCart", "GraduationCap", "Briefcase", "Heart", "Star", "Globe", "Zap"
];

const MODULOS_DISPONIBLES = [
  { id: "portal", label: "Portal de Socios" },
  { id: "erp", label: "ERP" },
  { id: "crm", label: "CRM" },
];

const SuperadminTenants = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantData, setTenantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTenant, setNewTenant] = useState({ tenant_id: "", nombre: "", subdominio: "" });

  // New servicio/beneficio
  const [newServicio, setNewServicio] = useState({ icono: "FileText", titulo: "", descripcion: "" });
  const [newBeneficio, setNewBeneficio] = useState({ texto: "" });

  const fetchTenants = useCallback(async () => {
    try {
      const res = await axios.get(API_URL + "/api/tenant/admin/list");
      setTenants(res.data);
    } catch (e) {
      toast.error("Error al cargar tenants");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTenantDetail = useCallback(async (tid) => {
    try {
      const res = await axios.get(API_URL + "/api/tenant/admin/" + tid);
      setTenantData(res.data);
    } catch (e) {
      toast.error("Error al cargar detalle del tenant");
    }
  }, []);

  useEffect(() => { fetchTenants(); }, [fetchTenants]);

  useEffect(() => {
    if (selectedTenant) fetchTenantDetail(selectedTenant);
  }, [selectedTenant, fetchTenantDetail]);

  if (user?.role !== "superadmin") {
    return <div className="text-center py-20 text-gray-500">Acceso restringido a superadmin.</div>;
  }

  const handleCreateTenant = async () => {
    if (!newTenant.tenant_id || !newTenant.nombre || !newTenant.subdominio) {
      toast.error("Completar todos los campos");
      return;
    }
    try {
      await axios.post(API_URL + "/api/tenant/admin/create", newTenant);
      toast.success("Tenant creado: " + newTenant.nombre);
      setCreateOpen(false);
      setNewTenant({ tenant_id: "", nombre: "", subdominio: "" });
      fetchTenants();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear tenant");
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const c = tenantData.config;
      await axios.put(API_URL + "/api/tenant/admin/" + selectedTenant + "/config", {
        nombre: c.nombre, logo_url: c.logo_url,
        color_primario: c.color_primario, color_secundario: c.color_secundario,
        color_acento: c.color_acento, color_cta: c.color_cta, activo: c.activo
      });
      toast.success("Config guardada");
    } catch (e) { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const saveLanding = async () => {
    setSaving(true);
    try {
      await axios.put(API_URL + "/api/tenant/admin/" + selectedTenant + "/landing", tenantData.landing);
      toast.success("Landing guardada");
    } catch (e) { toast.error("Error al guardar landing"); }
    finally { setSaving(false); }
  };

  const saveContacto = async () => {
    setSaving(true);
    try {
      await axios.put(API_URL + "/api/tenant/admin/" + selectedTenant + "/contacto", tenantData.contacto);
      toast.success("Contacto guardado");
    } catch (e) { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const saveFooter = async () => {
    setSaving(true);
    try {
      await axios.put(API_URL + "/api/tenant/admin/" + selectedTenant + "/footer", tenantData.footer);
      toast.success("Footer guardado");
    } catch (e) { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const saveModulos = async () => {
    setSaving(true);
    try {
      const mods = MODULOS_DISPONIBLES.map(m => ({
        modulo: m.id,
        activo: tenantData.modulos?.some(tm => tm.modulo === m.id && tm.activo) || false
      }));
      await axios.put(API_URL + "/api/tenant/admin/" + selectedTenant + "/modulos", mods);
      toast.success("Modulos guardados");
    } catch (e) { toast.error("Error"); }
    finally { setSaving(false); }
  };

  const addServicio = async () => {
    if (!newServicio.titulo) return;
    try {
      await axios.post(API_URL + "/api/tenant/admin/" + selectedTenant + "/servicios", {
        ...newServicio, orden: (tenantData.servicios?.length || 0) + 1
      });
      setNewServicio({ icono: "FileText", titulo: "", descripcion: "" });
      fetchTenantDetail(selectedTenant);
      toast.success("Servicio agregado");
    } catch (e) { toast.error("Error"); }
  };

  const deleteServicio = async (id) => {
    try {
      await axios.delete(API_URL + "/api/tenant/admin/" + selectedTenant + "/servicios/" + id);
      fetchTenantDetail(selectedTenant);
      toast.success("Servicio eliminado");
    } catch (e) { toast.error("Error"); }
  };

  const addBeneficio = async () => {
    if (!newBeneficio.texto) return;
    try {
      await axios.post(API_URL + "/api/tenant/admin/" + selectedTenant + "/beneficios", {
        ...newBeneficio, orden: (tenantData.beneficios?.length || 0) + 1
      });
      setNewBeneficio({ texto: "" });
      fetchTenantDetail(selectedTenant);
      toast.success("Beneficio agregado");
    } catch (e) { toast.error("Error"); }
  };

  const deleteBeneficio = async (id) => {
    try {
      await axios.delete(API_URL + "/api/tenant/admin/" + selectedTenant + "/beneficios/" + id);
      fetchTenantDetail(selectedTenant);
    } catch (e) { toast.error("Error"); }
  };

  const updateConfig = (field, value) => {
    setTenantData(prev => ({ ...prev, config: { ...prev.config, [field]: value } }));
  };
  const updateLanding = (field, value) => {
    setTenantData(prev => ({ ...prev, landing: { ...prev.landing, [field]: value } }));
  };
  const updateContacto = (field, value) => {
    setTenantData(prev => ({ ...prev, contacto: { ...prev.contacto, [field]: value } }));
  };
  const updateFooter = (field, value) => {
    setTenantData(prev => ({ ...prev, footer: { ...prev.footer, [field]: value } }));
  };
  const toggleModulo = (modId) => {
    setTenantData(prev => {
      const mods = [...(prev.modulos || [])];
      const idx = mods.findIndex(m => m.modulo === modId);
      if (idx >= 0) mods[idx] = { ...mods[idx], activo: !mods[idx].activo };
      else mods.push({ modulo: modId, activo: true });
      return { ...prev, modulos: mods };
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion de Empresas</h1>
          <p className="text-gray-500 text-sm">Administra los tenants de Cubiq</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="btn-crear-tenant">
          <Plus className="w-4 h-4 mr-2" />Nueva Empresa
        </Button>
      </div>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Tenant List */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-500">Empresas</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-4 text-center text-gray-400">Cargando...</div>
            ) : (
              <div className="divide-y">
                {tenants.map(t => (
                  <button key={t.tenant_id} onClick={() => setSelectedTenant(t.tenant_id)}
                    className={"w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors " +
                      (selectedTenant === t.tenant_id ? "bg-blue-50 border-l-2 border-blue-500" : "")}
                    data-testid={"tenant-" + t.tenant_id}>
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color_primario }}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{t.nombre}</p>
                      <p className="text-xs text-gray-400">{t.subdominio}.cubiq.com.ar</p>
                    </div>
                    <Badge variant={t.activo ? "default" : "secondary"} className="text-xs">
                      {t.activo ? "Activo" : "Inactivo"}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tenant Detail */}
        {tenantData ? (
          <Card>
            <CardContent className="p-0">
              <Tabs defaultValue="general">
                <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
                  <TabsTrigger value="general" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 px-6 py-3">
                    <Settings className="w-4 h-4 mr-2" />General
                  </TabsTrigger>
                  <TabsTrigger value="landing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 px-6 py-3">
                    <Globe className="w-4 h-4 mr-2" />Landing
                  </TabsTrigger>
                  <TabsTrigger value="servicios" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 px-6 py-3">
                    <FileText className="w-4 h-4 mr-2" />Cards/Beneficios
                  </TabsTrigger>
                  <TabsTrigger value="contacto" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 px-6 py-3">
                    <Phone className="w-4 h-4 mr-2" />Contacto/Footer
                  </TabsTrigger>
                  <TabsTrigger value="modulos" className="rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 px-6 py-3">
                    <Building2 className="w-4 h-4 mr-2" />Modulos
                  </TabsTrigger>
                </TabsList>

                {/* GENERAL */}
                <TabsContent value="general" className="p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div><Label>Nombre</Label><Input value={tenantData.config?.nombre || ""} onChange={e => updateConfig("nombre", e.target.value)} /></div>
                    <div><Label>Logo URL</Label><Input value={tenantData.config?.logo_url || ""} onChange={e => updateConfig("logo_url", e.target.value)} /></div>
                  </div>
                  <div>
                    <Label className="mb-3 block">Colores</Label>
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Primario</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={tenantData.config?.color_primario || "#2853B9"}
                            onChange={e => updateConfig("color_primario", e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                          <Input value={tenantData.config?.color_primario || ""} onChange={e => updateConfig("color_primario", e.target.value)} className="text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Secundario</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={tenantData.config?.color_secundario || "#1E3876"}
                            onChange={e => updateConfig("color_secundario", e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                          <Input value={tenantData.config?.color_secundario || ""} onChange={e => updateConfig("color_secundario", e.target.value)} className="text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Acento</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={tenantData.config?.color_acento || "#3DA1E5"}
                            onChange={e => updateConfig("color_acento", e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                          <Input value={tenantData.config?.color_acento || ""} onChange={e => updateConfig("color_acento", e.target.value)} className="text-xs" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">CTA (botones)</Label>
                        <div className="flex items-center gap-2">
                          <input type="color" value={tenantData.config?.color_cta || "#F97316"}
                            onChange={e => updateConfig("color_cta", e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                          <Input value={tenantData.config?.color_cta || ""} onChange={e => updateConfig("color_cta", e.target.value)} className="text-xs" />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Preview */}
                  <div className="border rounded-lg p-4 space-y-2">
                    <Label className="text-xs text-gray-500">Vista previa de colores</Label>
                    <div className="flex items-center gap-4">
                      <div className="rounded-lg p-4 text-white text-sm font-medium" style={{ backgroundColor: tenantData.config?.color_secundario }}>
                        Fondo secundario
                      </div>
                      <Button style={{ backgroundColor: tenantData.config?.color_primario }} className="text-white">Boton primario</Button>
                      <Button style={{ backgroundColor: tenantData.config?.color_cta }} className="text-white">CTA</Button>
                      <span style={{ color: tenantData.config?.color_acento }} className="font-bold">Texto acento</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={tenantData.config?.activo} onCheckedChange={v => updateConfig("activo", v)} />
                    <Label>Tenant activo</Label>
                  </div>
                  <Button onClick={saveConfig} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Guardando..." : "Guardar Config"}</Button>
                </TabsContent>

                {/* LANDING */}
                <TabsContent value="landing" className="p-6 space-y-6">
                  <Card><CardHeader><CardTitle className="text-base">Hero Section</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Badge texto</Label><Input value={tenantData.landing?.hero_badge_texto || ""} onChange={e => updateLanding("hero_badge_texto", e.target.value)} /></div>
                        <div><Label className="text-xs">Badge icono</Label>
                          <Select value={tenantData.landing?.hero_badge_icono || "Eye"} onValueChange={v => updateLanding("hero_badge_icono", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{ICONOS_DISPONIBLES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Titulo</Label><Input value={tenantData.landing?.hero_titulo || ""} onChange={e => updateLanding("hero_titulo", e.target.value)} /></div>
                        <div><Label className="text-xs">Titulo resaltado</Label><Input value={tenantData.landing?.hero_titulo_resaltado || ""} onChange={e => updateLanding("hero_titulo_resaltado", e.target.value)} /></div>
                      </div>
                      <div><Label className="text-xs">Subtitulo</Label><Textarea value={tenantData.landing?.hero_subtitulo || ""} onChange={e => updateLanding("hero_subtitulo", e.target.value)} rows={3} /></div>
                      <div><Label className="text-xs">Imagen URL</Label><Input value={tenantData.landing?.hero_imagen_url || ""} onChange={e => updateLanding("hero_imagen_url", e.target.value)} /></div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">CTA texto</Label><Input value={tenantData.landing?.hero_cta_texto || ""} onChange={e => updateLanding("hero_cta_texto", e.target.value)} /></div>
                        <div><Label className="text-xs">Login texto</Label><Input value={tenantData.landing?.hero_cta_login_texto || ""} onChange={e => updateLanding("hero_cta_login_texto", e.target.value)} /></div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Estadistica</Label><Input value={tenantData.landing?.hero_stat_texto || ""} onChange={e => updateLanding("hero_stat_texto", e.target.value)} /></div>
                        <div><Label className="text-xs">Subtexto stat</Label><Input value={tenantData.landing?.hero_stat_subtexto || ""} onChange={e => updateLanding("hero_stat_subtexto", e.target.value)} /></div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card><CardHeader><CardTitle className="text-base">Secciones</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Titulo "Sobre nosotros"</Label><Input value={tenantData.landing?.about_titulo || ""} onChange={e => updateLanding("about_titulo", e.target.value)} /></div>
                        <div><Label className="text-xs">Subtitulo</Label><Input value={tenantData.landing?.about_subtitulo || ""} onChange={e => updateLanding("about_subtitulo", e.target.value)} /></div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Titulo beneficios</Label><Input value={tenantData.landing?.beneficios_titulo || ""} onChange={e => updateLanding("beneficios_titulo", e.target.value)} /></div>
                        <div><Label className="text-xs">Subtitulo beneficios</Label><Input value={tenantData.landing?.beneficios_subtitulo || ""} onChange={e => updateLanding("beneficios_subtitulo", e.target.value)} /></div>
                      </div>
                      <div><Label className="text-xs">Imagen beneficios URL</Label><Input value={tenantData.landing?.beneficios_imagen_url || ""} onChange={e => updateLanding("beneficios_imagen_url", e.target.value)} /></div>
                    </CardContent>
                  </Card>
                  <Card><CardHeader><CardTitle className="text-base">CTA y Login</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Titulo CTA</Label><Input value={tenantData.landing?.cta_titulo || ""} onChange={e => updateLanding("cta_titulo", e.target.value)} /></div>
                        <div><Label className="text-xs">Boton CTA</Label><Input value={tenantData.landing?.cta_boton_texto || ""} onChange={e => updateLanding("cta_boton_texto", e.target.value)} /></div>
                      </div>
                      <div><Label className="text-xs">Subtitulo CTA</Label><Textarea value={tenantData.landing?.cta_subtitulo || ""} onChange={e => updateLanding("cta_subtitulo", e.target.value)} rows={2} /></div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Login subtitulo</Label><Input value={tenantData.landing?.login_subtitulo || ""} onChange={e => updateLanding("login_subtitulo", e.target.value)} /></div>
                        <div><Label className="text-xs">Login imagen URL</Label><Input value={tenantData.landing?.login_imagen_url || ""} onChange={e => updateLanding("login_imagen_url", e.target.value)} /></div>
                      </div>
                      <div><Label className="text-xs">Mensaje portal login</Label><Input value={tenantData.landing?.login_mensaje_portal || ""} onChange={e => updateLanding("login_mensaje_portal", e.target.value)} /></div>
                    </CardContent>
                  </Card>
                  <Button onClick={saveLanding} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Guardando..." : "Guardar Landing"}</Button>
                </TabsContent>

                {/* SERVICIOS + BENEFICIOS */}
                <TabsContent value="servicios" className="p-6 space-y-6">
                  <Card><CardHeader><CardTitle className="text-base">Cards de Servicios</CardTitle><CardDescription>Aparecen en la seccion "Sobre nosotros" de la landing</CardDescription></CardHeader>
                    <CardContent className="space-y-3">
                      {tenantData.servicios?.map(s => (
                        <div key={s.id} className="flex items-center gap-3 p-2 border rounded-lg">
                          <Badge variant="outline">{s.icono}</Badge>
                          <span className="font-medium flex-1">{s.titulo}</span>
                          <span className="text-sm text-gray-500 truncate max-w-xs">{s.descripcion}</span>
                          <Button variant="ghost" size="icon" onClick={() => deleteServicio(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      ))}
                      <div className="border-t pt-3 mt-3">
                        <p className="text-sm font-medium mb-2">Agregar servicio</p>
                        <div className="grid grid-cols-[100px_1fr_2fr_auto] gap-2 items-end">
                          <Select value={newServicio.icono} onValueChange={v => setNewServicio(p => ({ ...p, icono: v }))}>
                            <SelectTrigger className="text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>{ICONOS_DISPONIBLES.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input placeholder="Titulo" value={newServicio.titulo} onChange={e => setNewServicio(p => ({ ...p, titulo: e.target.value }))} />
                          <Input placeholder="Descripcion" value={newServicio.descripcion} onChange={e => setNewServicio(p => ({ ...p, descripcion: e.target.value }))} />
                          <Button size="sm" onClick={addServicio}><Plus className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card><CardHeader><CardTitle className="text-base">Beneficios</CardTitle><CardDescription>Lista de beneficios con checkbox verde</CardDescription></CardHeader>
                    <CardContent className="space-y-3">
                      {tenantData.beneficios?.map(b => (
                        <div key={b.id} className="flex items-center gap-3 p-2 border rounded-lg">
                          <span className="flex-1 text-sm">{b.texto}</span>
                          <Button variant="ghost" size="icon" onClick={() => deleteBeneficio(b.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      ))}
                      <div className="border-t pt-3 mt-3 flex gap-2">
                        <Input placeholder="Texto del beneficio" value={newBeneficio.texto} onChange={e => setNewBeneficio({ texto: e.target.value })} className="flex-1" />
                        <Button size="sm" onClick={addBeneficio}><Plus className="w-4 h-4" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* CONTACTO + FOOTER */}
                <TabsContent value="contacto" className="p-6 space-y-6">
                  <Card><CardHeader><CardTitle className="text-base">Contacto</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Telefono</Label><Input value={tenantData.contacto?.telefono || ""} onChange={e => updateContacto("telefono", e.target.value)} /></div>
                        <div><Label className="text-xs">Email</Label><Input value={tenantData.contacto?.email || ""} onChange={e => updateContacto("email", e.target.value)} /></div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Direccion linea 1</Label><Input value={tenantData.contacto?.direccion_linea1 || ""} onChange={e => updateContacto("direccion_linea1", e.target.value)} /></div>
                        <div><Label className="text-xs">Direccion linea 2</Label><Input value={tenantData.contacto?.direccion_linea2 || ""} onChange={e => updateContacto("direccion_linea2", e.target.value)} /></div>
                      </div>
                      <Button onClick={saveContacto} disabled={saving}><Save className="w-4 h-4 mr-2" />Guardar Contacto</Button>
                    </CardContent>
                  </Card>
                  <Card><CardHeader><CardTitle className="text-base">Footer</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      <div><Label className="text-xs">Copyright</Label><Input value={tenantData.footer?.copyright_texto || ""} onChange={e => updateFooter("copyright_texto", e.target.value)} /></div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Website URL</Label><Input value={tenantData.footer?.website_url || ""} onChange={e => updateFooter("website_url", e.target.value)} /></div>
                        <div><Label className="text-xs">Website label</Label><Input value={tenantData.footer?.website_label || ""} onChange={e => updateFooter("website_label", e.target.value)} /></div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        <div><Label className="text-xs">Powered by logo URL</Label><Input value={tenantData.footer?.powered_by_logo || ""} onChange={e => updateFooter("powered_by_logo", e.target.value)} /></div>
                        <div><Label className="text-xs">Powered by URL</Label><Input value={tenantData.footer?.powered_by_url || ""} onChange={e => updateFooter("powered_by_url", e.target.value)} /></div>
                      </div>
                      <Button onClick={saveFooter} disabled={saving}><Save className="w-4 h-4 mr-2" />Guardar Footer</Button>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* MODULOS */}
                <TabsContent value="modulos" className="p-6 space-y-6">
                  <Card><CardHeader><CardTitle className="text-base">Modulos Habilitados</CardTitle><CardDescription>Activa o desactiva modulos para este tenant</CardDescription></CardHeader>
                    <CardContent className="space-y-4">
                      {MODULOS_DISPONIBLES.map(m => (
                        <div key={m.id} className="flex items-center justify-between p-3 border rounded-lg">
                          <div>
                            <p className="font-medium">{m.label}</p>
                            <p className="text-xs text-gray-500">{m.id}</p>
                          </div>
                          <Switch
                            checked={tenantData.modulos?.some(tm => tm.modulo === m.id && tm.activo)}
                            onCheckedChange={() => toggleModulo(m.id)} />
                        </div>
                      ))}
                      <Button onClick={saveModulos} disabled={saving}><Save className="w-4 h-4 mr-2" />Guardar Modulos</Button>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        ) : (
          <Card className="flex items-center justify-center min-h-[400px]">
            <div className="text-center text-gray-400">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Selecciona una empresa para editar</p>
            </div>
          </Card>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva Empresa</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><Label>Tenant ID (slug unico)</Label><Input placeholder="sae" value={newTenant.tenant_id} onChange={e => setNewTenant(p => ({ ...p, tenant_id: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} /></div>
            <div><Label>Nombre completo</Label><Input placeholder="Sociedad Argentina de Emergentologia" value={newTenant.nombre} onChange={e => setNewTenant(p => ({ ...p, nombre: e.target.value }))} /></div>
            <div><Label>Subdominio</Label>
              <div className="flex items-center gap-1"><Input placeholder="sae" value={newTenant.subdominio} onChange={e => setNewTenant(p => ({ ...p, subdominio: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))} /><span className="text-sm text-gray-500 whitespace-nowrap">.cubiq.com.ar</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateTenant} data-testid="btn-confirmar-crear-tenant"><Plus className="w-4 h-4 mr-2" />Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperadminTenants;
